import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useCache } from '../contexts/DataCacheContext';
import { saveFreeWorkout, awardWorkoutXP, evaluateBadgesAfterWorkout } from '../lib/database';
import { buildWorkoutSummary } from '../lib/workoutSummary';
import { shouldShowPushPrePrompt, markPushPrePromptDone } from '../lib/pushPrePrompt';
import RestPushPrePrompt from './RestPushPrePrompt';
import { supabase } from '../lib/supabase';
import { calculateWorkoutXP } from '../lib/xpUtils';
import ExerciseExecutionCard from './ExerciseExecutionCard';
import { isTimeType } from '../lib/exerciseType';
import RestTimer, { clearPersistedRestTimer } from './RestTimer';
import AddExerciseSheet from './AddExerciseSheet';
import SupersetLinkBar from './SupersetLinkBar';
import SupersetList from './SupersetList';
import AddActionsSheet from './AddActionsSheet';
import { useSupersetLinking } from '../hooks/useSupersetLinking';
import { getSupersetForExercise, sharedRounds, isRoundComplete, resolveRestSeconds } from '../lib/sessionSupersets';
import { unlockAudioAlert } from '../lib/restAlert';
import { cancelPushForRest } from '../lib/restPush';
import { newClientSessionId } from '../lib/clientSessionId';

function FreeWorkout() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const toast = useToast();
  const { invalidate: invalidateCache } = useCache();

  const [startTime, setStartTime] = useState(Date.now());
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [exercises, setExercises] = useState([]);
  const [exerciseStates, setExerciseStates] = useState({});
  const [sessionSupersets, setSessionSupersets] = useState([]); // superserie di sessione (effimere)
  const linking = useSupersetLinking({
    sessionSupersets,
    setSessionSupersets,
    orderedIds: exercises.map(e => e.id), // PB-008: ordine reale mostrato a schermo
  });
  const [showAddExerciseModal, setShowAddExerciseModal] = useState(true); // Start with modal open
  const [showAddMenu, setShowAddMenu] = useState(false); // menu del pulsante "+"
  const [saving, setSaving] = useState(false);
  // Guardia sincrona contro il doppio submit (lo stato React si aggiorna
  // solo al re-render: un secondo tap nello stesso frame lo bypasserebbe)
  const savingRef = useRef(false);
  // PB-013: identità stabile della sessione (chiave di idempotency DB).
  // Generata UNA volta (lazy init, mai nei render); il restore adotta quella
  // salvata se esiste; sessioni legacy senza UUID mantengono quella nuova
  // (lazy migration sicura: una sessione ancora in localStorage non è mai
  // stata salvata sul server). Muore col clear della sessione.
  const [clientSessionId, setClientSessionId] = useState(() => newClientSessionId());

  // Rest Timer state
  const [isRestTimerActive, setIsRestTimerActive] = useState(false);
  const [restTimerSeconds, setRestTimerSeconds] = useState(90);
  const [timerKey, setTimerKey] = useState(0);
  // Pre-prompt notifiche: proposto al primo recupero della vita dell'utente
  const [showPushPrePrompt, setShowPushPrePrompt] = useState(false);

  const STORAGE_KEY = `free-workout-session-${profile?.id}`;

  // Timer effect
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  // Auto-save to localStorage
  useEffect(() => {
    if (exercises.length > 0) {
      const saveData = {
        startTime,
        exercises,
        exerciseStates,
        sessionSupersets, // superserie di sessione
        clientSessionId, // PB-013: identità della sessione, stabile tra reload/retry
        lastUpdate: Date.now()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(saveData));
    }
  }, [exercises, exerciseStates, sessionSupersets, startTime, STORAGE_KEY, clientSessionId]);

  // Restore session on mount
  useEffect(() => {
    const savedSession = localStorage.getItem(STORAGE_KEY);
    if (savedSession) {
      try {
        const sessionData = JSON.parse(savedSession);
        const sessionAge = Date.now() - sessionData.lastUpdate;

        // Restore if session < 24 hours old
        if (sessionAge < 24 * 60 * 60 * 1000) {
          console.log('♻️ [FREE WORKOUT] Restoring saved session');
          setStartTime(sessionData.startTime);
          setExercises(sessionData.exercises || []);
          setExerciseStates(sessionData.exerciseStates || {});
          setSessionSupersets(sessionData.sessionSupersets || []);
          // PB-013: riprendi l'identità della sessione salvata; se assente
          // (sessione legacy) resta quella appena generata (lazy migration).
          if (sessionData.clientSessionId) {
            setClientSessionId(sessionData.clientSessionId);
          }
          setShowAddExerciseModal(false);
        }
      } catch (e) {
        console.error('Error parsing saved session:', e);
      }
    }
  }, [STORAGE_KEY]);

  // Warn before leaving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasCompletedSets()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [exerciseStates]);

  const handleAddExercise = ({ name, sets, target, weight, templateId, restSeconds, exerciseType, usesWeight }) => {
    const tempId = `free-${Date.now()}`;
    const type = exerciseType || 'reps';
    const isTimeBased = isTimeType(type); // 'time' | 'hold'

    const newExercise = {
      id: tempId,
      name,
      sets: parseInt(sets),
      reps: target, // per time/hold contiene i secondi target (placeholder card)
      weight: weight ? parseFloat(weight) : null,
      exercise_template_id: templateId,
      rest_seconds: restSeconds ? parseInt(restSeconds) : 90,
      exercise_type: type,
      uses_weight: usesWeight // rispettato da ExerciseExecutionCard (helper usesWeight)
    };

    setExercises(prev => [...prev, newExercise]);

    // Initialize state for this exercise
    const initialSets = Array.from({ length: parseInt(sets) }, (_, i) => ({
      setNumber: i + 1,
      completed: false,
      reps: '',
      duration: isTimeBased ? '' : undefined,
      weight: weight || '',
      notes: '',
      expanded: false
    }));

    setExerciseStates(prev => ({
      ...prev,
      [tempId]: { sets: initialSets }
    }));

    setShowAddExerciseModal(false);
  };

  // Auto-fill dei campi vuoti quando una serie viene completata
  const buildToggledSet = (set, exercise, willBeCompleted) => {
    let updatedSet = { ...set, completed: willBeCompleted };
    if (willBeCompleted) {
      if (!updatedSet.reps || updatedSet.reps === '') {
        updatedSet.reps = exercise?.reps || '';
      }
      if (!updatedSet.weight || updatedSet.weight === '') {
        updatedSet.weight = exercise?.weight || '';
      }
    }
    return updatedSet;
  };

  // Decisione recupero (deterministica, sullo stato mergiato previsto).
  // Superserie: il recupero parte solo a fine giro.
  const maybeStartRest = (exerciseId, exercise, setNumber, statesAfter) => {
    const group = getSupersetForExercise(sessionSupersets, exerciseId);
    if (group) {
      const roundIndex = setNumber - 1;
      const shared = sharedRounds(group, statesAfter);
      const roundDone = roundIndex < shared && isRoundComplete(group, statesAfter, roundIndex);
      const beyondShared = roundIndex >= shared;
      if (roundDone || beyondShared) {
        // PB-006 + PB-011 (J4):
        // A superset never invents a default rest.
        // L'UNICA fonte è il recupero della superserie; se null/0 nessun timer.
        // Non si torna MAI al recupero individuale di un membro (anche oltre i
        // round condivisi, finché l'esercizio resta nel gruppo). Nessun 90.
        const rest = Number(resolveRestSeconds(group));
        if (rest > 0) handleStartRestTimer(rest);
      }
    } else if (exercise?.rest_seconds) {
      handleStartRestTimer(exercise.rest_seconds);
    }
  };

  const handleToggleSet = (exerciseId, setNumber) => {
    const exercise = exercises.find(ex => ex.id === exerciseId);
    const currentSet = exerciseStates[exerciseId]?.sets?.find(s => s.setNumber === setNumber);
    if (!currentSet) return;
    const willBeCompleted = !currentSet.completed;

    // Updater FUNZIONALE: con l'oggetto pre-calcolato più toggle nello stesso
    // batch si sovrascrivevano a vicenda (restava solo l'ultimo).
    const applyToggle = (states) => {
      if (!states[exerciseId]?.sets) return states;
      return {
        ...states,
        [exerciseId]: {
          ...states[exerciseId],
          sets: states[exerciseId].sets.map(set =>
            set.setNumber === setNumber ? buildToggledSet(set, exercise, willBeCompleted) : set
          )
        }
      };
    };
    setExerciseStates(applyToggle);

    if (willBeCompleted) {
      maybeStartRest(exerciseId, exercise, setNumber, applyToggle(exerciseStates));
    }
  };

  // Spunta master dell'esercizio: completa/azzera TUTTE le serie in un unico
  // aggiornamento atomico; il recupero parte UNA volta, riferito all'ultima serie.
  const handleToggleAllSets = (exerciseId, newCompletedState) => {
    const exercise = exercises.find(ex => ex.id === exerciseId);
    const sets = exerciseStates[exerciseId]?.sets;
    if (!sets || sets.length === 0) return;

    const applyToggleAll = (states) => {
      if (!states[exerciseId]?.sets) return states;
      return {
        ...states,
        [exerciseId]: {
          ...states[exerciseId],
          sets: states[exerciseId].sets.map(set =>
            set.completed === newCompletedState ? set : buildToggledSet(set, exercise, newCompletedState)
          )
        }
      };
    };
    setExerciseStates(applyToggleAll);

    if (newCompletedState) {
      const lastSetNumber = Math.max(...sets.map(s => s.setNumber));
      maybeStartRest(exerciseId, exercise, lastSetNumber, applyToggleAll(exerciseStates));
    }
  };

  const handleExpandSet = (exerciseId, setNumber) => {
    setExerciseStates(prev => ({
      ...prev,
      [exerciseId]: {
        ...prev[exerciseId],
        sets: prev[exerciseId].sets.map(set =>
          set.setNumber === setNumber
            ? { ...set, expanded: !set.expanded }
            : set
        )
      }
    }));
  };

  const handleUpdateSet = (exerciseId, setNumber, field, value) => {
    setExerciseStates(prev => ({
      ...prev,
      [exerciseId]: {
        ...prev[exerciseId],
        sets: prev[exerciseId].sets.map(set =>
          set.setNumber === setNumber
            ? { ...set, [field]: value }
            : set
        )
      }
    }));
  };

  const handleAddExtraSet = (exerciseId) => {
    setExerciseStates(prev => {
      const currentSets = prev[exerciseId].sets;
      const nextSetNumber = currentSets.length + 1;
      const lastCompletedSet = [...currentSets].reverse().find(s => s.completed && s.weight);
      const suggestedWeight = lastCompletedSet ? lastCompletedSet.weight : '';

      return {
        ...prev,
        [exerciseId]: {
          ...prev[exerciseId],
          sets: [
            ...currentSets,
            {
              setNumber: nextSetNumber,
              completed: false,
              reps: '',
              weight: suggestedWeight,
              notes: '',
              expanded: false,
              isExtra: true
            }
          ]
        }
      };
    });
  };

  const handleDeleteSet = (exerciseId, setNumber) => {
    const currentSets = exerciseStates[exerciseId]?.sets || [];
    const remaining = currentSets.filter(s => s.setNumber !== setNumber);

    if (remaining.length === 0) {
      // Remove the whole exercise
      setExercises(prev => prev.filter(ex => ex.id !== exerciseId));
      setExerciseStates(prev => {
        const { [exerciseId]: _removed, ...rest } = prev;
        return rest;
      });
    } else {
      setExerciseStates(prev => ({
        ...prev,
        [exerciseId]: {
          ...prev[exerciseId],
          sets: remaining.map((s, i) => ({ ...s, setNumber: i + 1 }))
        }
      }));
    }
  };

  const getProgress = () => {
    let total = 0;
    let completed = 0;

    Object.values(exerciseStates).forEach(ex => {
      total += ex.sets.length;
      completed += ex.sets.filter(s => s.completed).length;
    });

    return { total, completed };
  };

  const hasCompletedSets = () => {
    return Object.values(exerciseStates).some(ex =>
      ex.sets.some(set => set.completed)
    );
  };

  const handleCompleteWorkout = async () => {
    if (!hasCompletedSets()) {
      toast.warning('Completa almeno una serie prima di salvare l\'allenamento!');
      return;
    }

    // Prevent double-tap (ref = guardia sincrona: lo stato React non è ancora
    // aggiornato se il secondo tap arriva nello stesso frame)
    if (saving || savingRef.current) {
      console.log('⚠️ [FREE WORKOUT] Already saving, ignoring duplicate tap');
      return;
    }

    if (!window.confirm('Vuoi completare questo allenamento libero?')) {
      return;
    }

    if (savingRef.current) return; // tap accodato durante il confirm
    savingRef.current = true;
    setSaving(true);
    console.log('💾 [FREE WORKOUT] Saving workout...');

    const exerciseLogs = [];

    Object.entries(exerciseStates).forEach(([exerciseId, exState]) => {
      const exercise = exercises.find(ex => ex.id === exerciseId);
      const isTimeBased = isTimeType(exercise?.exercise_type);

      exState.sets.forEach(set => {
        const hasData = set.completed || set.reps || set.weight;
        if (!hasData) return;

        if (isTimeBased) {
          // For time-based: "reps" field stores duration in seconds
          const durationSeconds = set.reps ? parseInt(set.reps) : (exercise?.reps ? parseInt(exercise.reps) : null);
          exerciseLogs.push({
            exerciseId: null,
            exerciseTemplateId: exercise?.exercise_template_id || null,
            exerciseName: exercise?.name || 'Sconosciuto',
            setNumber: set.setNumber,
            reps: 1,
            weight: set.weight ? parseFloat(set.weight) : null,
            durationSeconds,
            notes: set.notes || null,
            isExtra: set.isExtra || false
          });
        } else {
          let repsValue = set.reps ? parseInt(set.reps) : null;
          if (!repsValue && set.completed && exercise) {
            const match = exercise.reps?.toString().match(/\d+/);
            repsValue = match ? parseInt(match[0]) : null;
          }
          if (repsValue !== null) {
            exerciseLogs.push({
              exerciseId: null,
              exerciseTemplateId: exercise?.exercise_template_id || null,
              exerciseName: exercise?.name || 'Sconosciuto',
              setNumber: set.setNumber,
              reps: repsValue,
              weight: set.weight ? parseFloat(set.weight) : null,
              notes: set.notes || null,
              isExtra: set.isExtra || false
            });
          }
        }
      });
    });

    const durationMinutes = Math.floor(elapsedSeconds / 60);

    // Pre-refresh token before saving (token may have expired during long workout)
    try {
      console.log('🔄 [FREE WORKOUT] Refreshing session before save...');
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.warn('⚠️ [FREE WORKOUT] Session refresh warning:', error.message);
      } else if (data?.session) {
        console.log('✅ [FREE WORKOUT] Session refreshed successfully');
      }
    } catch (refreshErr) {
      console.warn('⚠️ [FREE WORKOUT] Could not refresh session:', refreshErr.message);
    }

    // PHASE 1: Core save (critical - must succeed)
    let workoutLog = null;
    try {
      // PB-013: salvataggio atomico/idempotente via RPC (clientSessionId = chiave)
      workoutLog = await saveFreeWorkout(profile.id, durationMinutes, null, exerciseLogs, clientSessionId);
      console.log('✅ [FREE WORKOUT] Workout saved successfully');
    } catch (error) {
      console.error('❌ [FREE WORKOUT] Error saving workout:', error);
      toast.error('Errore nel salvataggio dell\'allenamento. Riprova.');
      savingRef.current = false;
      setSaving(false);
      return;
    }

    // PHASE 2: Post-save cleanup (non-critical - failures are silent)
    try {
      localStorage.removeItem(STORAGE_KEY);
      // Workout chiuso: nessun recupero deve sopravvivere (né timer persistito
      // né push programmata, che arriverebbe ad allenamento finito)
      setIsRestTimerActive(false);
      clearPersistedRestTimer();
      cancelPushForRest();
    } catch (e) {
      console.warn('⚠️ [FREE WORKOUT] Could not clear localStorage:', e);
    }

    // PHASE 3: Cache invalidation (non-critical)
    try {
      invalidateCache(`athlete-dashboard-*`);
      invalidateCache(`athlete-workout-logs-*`);
      invalidateCache(`athlete-volume-*`);
      invalidateCache(`athlete-exercise-records-*`);
    } catch (e) {
      console.warn('⚠️ [FREE WORKOUT] Cache invalidation warning:', e);
    }

    // Riepilogo dai dati già in memoria: il popup compare SEMPRE,
    // anche se XP o badge falliscono.
    const workoutSummary = buildWorkoutSummary(exerciseLogs, durationMinutes);

    // PHASE 4: Award XP (non-blocking)
    let xpResult = null;
    try {
      const xpBreakdown = calculateWorkoutXP({ durationMinutes, completedSets: exerciseLogs.length });
      xpResult = await awardWorkoutXP(profile.id, workoutLog.id, xpBreakdown.total);
      xpResult.xpBreakdown = xpBreakdown;
      console.log('⭐ [XP] Awarded', xpBreakdown.total, 'XP → Level', xpResult.currentLevel);
    } catch (xpErr) {
      console.warn('⚠️ [XP] Could not award XP (non-blocking):', xpErr.message);
    }

    // PHASE 4b: Badge subito dopo XP (retry → [] → nessuna doppia notifica)
    let newBadges = [];
    try {
      newBadges = await evaluateBadgesAfterWorkout(profile.id);
    } catch (badgeErr) {
      console.warn('⚠️ [BADGE] Could not evaluate badges (non-blocking):', badgeErr.message);
    }

    // PHASE 5: Navigate (workout is saved, always navigate)
    savingRef.current = false;
    setSaving(false);
    navigate('/athlete', { state: { workoutCompleted: true, xpResult, workoutSummary, newBadges, workoutName: 'Allenamento libero' } });
  };

  const handleExit = () => {
    if (hasCompletedSets()) {
      if (!window.confirm('Hai dati non salvati. Sei sicuro di voler uscire?')) {
        return;
      }
    }

    localStorage.removeItem(STORAGE_KEY);
    // Anche il recupero in corso muore con il workout (timer + push)
    clearPersistedRestTimer();
    cancelPushForRest();
    navigate('/athlete');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartRestTimer = (seconds = 90) => {
    // Gesto utente → sblocca l'audio iOS per il beep di fine recupero
    unlockAudioAlert();
    // Nuova serie = nuovo recupero: azzera lo stato persistito PRIMA del
    // remount, altrimenti RestTimer ripristina il countdown precedente
    // (vecchia scadenza/timerId, push non riprogrammata). La push vecchia
    // muore con il nuovo timerId (upsert su rest_push_state per user_id).
    clearPersistedRestTimer();
    setRestTimerSeconds(seconds);
    setTimerKey(prev => prev + 1);
    setIsRestTimerActive(true);
    // Primo recupero in assoluto → proponi le notifiche (una volta sola)
    if (shouldShowPushPrePrompt()) setShowPushPrePrompt(true);
  };

  const handleCloseRestTimer = () => {
    setIsRestTimerActive(false);
  };

  const handleClosePushPrePrompt = () => {
    // Qualunque esito (Attiva / Non ora / chiusura): mai riproporre in automatico
    markPushPrePromptDone();
    setShowPushPrePrompt(false);
  };

  const progress = getProgress();
  const canComplete = hasCompletedSets();

  // Render helper: card di un esercizio (usata sia standalone sia nel blocco superserie)
  const renderFreeCard = (exercise) => (
    <ExerciseExecutionCard
      key={exercise.id}
      exercise={exercise}
      setStates={exerciseStates[exercise.id]?.sets || []}
      onToggleSet={(setNumber) => handleToggleSet(exercise.id, setNumber)}
      onToggleAllSets={(newCompletedState) => handleToggleAllSets(exercise.id, newCompletedState)}
      onExpandSet={(setNumber) => handleExpandSet(exercise.id, setNumber)}
      onUpdateSet={(setNumber, field, value) => handleUpdateSet(exercise.id, setNumber, field, value)}
      onAddExtraSet={() => handleAddExtraSet(exercise.id)}
      onDeleteSet={(setNumber) => handleDeleteSet(exercise.id, setNumber)}
      onStartRestTimer={() => handleStartRestTimer(exercise.rest_seconds || 90)}
      showDeleteAll={true}
    />
  );

  return (
    <>
      {isRestTimerActive && ReactDOM.createPortal(
        <RestTimer
          key={timerKey}
          isActive={true}
          onClose={handleCloseRestTimer}
          initialSeconds={restTimerSeconds}
        />,
        document.body
      )}

      {/* Pre-prompt notifiche (solo al primo recupero di sempre) */}
      {showPushPrePrompt && <RestPushPrePrompt onClose={handleClosePushPrePrompt} />}

      <div className="min-h-screen bg-gray-50 flex flex-col">
        <header className="sticky top-0 bg-white shadow-lg z-50 px-4 py-4">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            <button
              onClick={handleExit}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg font-semibold text-sm hover:bg-red-100 transition-colors"
            >
              ← Esci
            </button>
            <div className="flex flex-col items-center">
              <h1 className="text-xl font-extrabold text-gray-900 uppercase tracking-wide">Allenamento Libero</h1>
              <div className="font-mono text-2xl font-bold text-primary-500 tabular-nums">
                {formatTime(elapsedSeconds)}
              </div>
            </div>
            <div className="w-20"></div>
          </div>
        </header>

        <main className="flex-1 w-full max-w-4xl mx-auto px-4 py-5" style={{ paddingBottom: 'max(260px, calc(220px + env(safe-area-inset-bottom, 0px)))' }}>
          <div className="flex flex-col gap-4">
            {exercises.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🏋️</div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">Inizia il tuo allenamento</h2>
                <p className="text-gray-600 mb-6">Aggiungi il primo esercizio per iniziare</p>
                <button
                  onClick={() => setShowAddExerciseModal(true)}
                  className="px-6 py-3 gradient-primary text-white rounded-xl font-bold shadow-lg"
                >
                  ➕ Aggiungi Esercizio
                </button>
              </div>
            ) : (
              <SupersetList
                items={exercises}
                sessionSupersets={sessionSupersets}
                renderCard={renderFreeCard}
                linking={linking}
              />
            )}
          </div>
        </main>

        <footer className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white to-white/0 px-4 pt-5 shadow-2xl" style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom, 20px))' }}>
          <div className="max-w-4xl mx-auto space-y-3">
            {/* PB-009: durante la modalità collega superserie il footer ospita la
                barra di conferma (sempre visibile in basso); progress e COMPLETA
                tornano appena si esce/conferma. */}
            {linking.linkMode ? (
              <SupersetLinkBar
                linkMode={linking.linkMode}
                count={linking.selection.length}
                onConfirm={linking.confirm}
                onCancel={linking.cancel}
              />
            ) : (
            <>
            {exercises.length > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-700">
                    {progress.completed}/{progress.total} serie
                  </span>
                  <span className="text-sm font-bold text-primary-500">
                    {progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0}%
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full gradient-success transition-all duration-500 ease-out"
                    style={{ width: `${progress.total > 0 ? (progress.completed / progress.total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            )}

            <button
              onClick={() => (exercises.length > 0 ? setShowAddMenu(true) : setShowAddExerciseModal(true))}
              className="w-full py-3 px-4 bg-white text-primary-600 border-2 border-primary-500 rounded-xl font-semibold text-sm hover:bg-primary-50 transition-all active:scale-98"
            >
              ➕ Aggiungi
            </button>

            {exercises.length > 0 && (
              <button
                onClick={handleCompleteWorkout}
                disabled={!canComplete || saving}
                className="w-full py-4 px-6 gradient-success text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-98"
              >
                {saving ? 'Salvataggio...' : '✓ COMPLETA ALLENAMENTO'}
              </button>
            )}
            </>
            )}
          </div>
        </footer>

        {showAddMenu && (
          <AddActionsSheet
            onClose={() => setShowAddMenu(false)}
            canCreateSuperset={exercises.length >= 2}
            onAddExercise={() => { setShowAddMenu(false); setShowAddExerciseModal(true); }}
            onCreateSuperset={() => { setShowAddMenu(false); linking.enter(); }}
          />
        )}

        {showAddExerciseModal && (
          <AddExerciseSheet
            title="Aggiungi Esercizio"
            onClose={() => setShowAddExerciseModal(false)}
            onAdd={handleAddExercise}
            profile={profile}
            isRestTimerActive={isRestTimerActive}
            defaultSets="1"
            defaultRestSeconds="90"
          />
        )}
      </div>
    </>
  );
}

export default FreeWorkout;
